import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@mantine/core';
import { useHasRole, type LftRole } from '@/auth/useRequireRole';

interface RoleGateProps {
  role: LftRole | LftRole[];
  children: ReactNode;
  fallback?: ReactNode;
  showHint?: boolean;
}

/**
 * Renders children only when the current user holds at least one of the
 * required roles. For read-only users this is the standard mechanism to
 * hide write actions (`null` fallback).
 */
export function RoleGate({ role, children, fallback = null, showHint = false }: RoleGateProps) {
  const { t } = useTranslation('errors');
  const allowed = useHasRole(role);
  if (allowed) return <>{children}</>;
  if (showHint) {
    return (
      <Alert color="gray" variant="light" title={t('missingRole.title')}>
        {t('missingRole.description')}
      </Alert>
    );
  }
  return <>{fallback}</>;
}
